import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import BookCallModal from "../../components/feature/BookCallModal";

const industries = [
  {
    id: "home-services",
    icon: "ri-hammer-line",
    name: "Home Services",
    tagline: "Capture every job. Never miss a call again.",
    painPoints: [
      "Missed calls while your team is on jobs",
      "No systematic follow-up on inbound leads",
    ],
    solution: "AI answers, books, and follows up instantly.",
    outcome: "More booked jobs without hiring staff.",
    image: "https://public.readdy.ai/ai/img_res/9bd8c9bbb0cd2d65cc6a7a6eaeb929a2.jpg",
    metrics: [
      { value: "+30%", label: "Booked Jobs" },
      { value: "24/7", label: "Lead Coverage" },
    ],
  },
  {
    id: "legal",
    icon: "ri-scales-3-line",
    name: "Legal",
    tagline: "Qualify faster. Win better clients.",
    painPoints: [
      "Slow intake process losing high-value clients",
      "Unqualified leads wasting attorney time",
    ],
    solution: "Automated intake and qualification workflows.",
    outcome: "Better clients, faster conversion.",
    image: "https://readdy.ai/api/search-image?query=professional%20modern%20law%20firm%20office%20clean%20minimal%20elegant%20lawyer%20desk%20wood%20conference%20room%20bright%20natural%20light%20contemporary%20interior%20design%20premium%20corporate%20professional&width=700&height=500&seq=ind-legal-1&orientation=landscape",
    metrics: [
      { value: "3x", label: "Faster Intake" },
      { value: "60%", label: "Less Admin Work" },
    ],
  },
  {
    id: "medical-medspa",
    icon: "ri-hospital-line",
    name: "Medical & Medspa",
    tagline: "Full calendars. Higher retention.",
    painPoints: [
      "Empty appointment slots losing monthly revenue",
      "Poor rebooking rates after initial visits",
    ],
    solution: "Smart scheduling + follow-up systems.",
    outcome: "Full calendars and higher retention.",
    image: "https://readdy.ai/api/search-image?query=modern%20clean%20medical%20medspa%20aesthetic%20clinic%20interior%20white%20minimal%20bright%20contemporary%20wellness%20beauty%20treatment%20room%20professional%20medical%20clean%20aesthetic%20modern%20design&width=700&height=500&seq=ind-medspa-1&orientation=landscape",
    metrics: [
      { value: "40%", label: "Fewer No-Shows" },
      { value: "2x", label: "Rebooking Rate" },
    ],
  },
  {
    id: "financial-services",
    icon: "ri-bank-line",
    name: "Financial Services",
    tagline: "Consistent deal flow. Zero manual pipelines.",
    painPoints: [
      "Lead leakage through slow follow-up",
      "Manual pipeline management eating team bandwidth",
    ],
    solution: "Automated pipeline management.",
    outcome: "Consistent deal flow.",
    image: "https://readdy.ai/api/search-image?query=modern%20professional%20financial%20services%20office%20clean%20minimal%20corporate%20enterprise%20desk%20advisor%20meeting%20bright%20contemporary%20interior%20boardroom%20premium%20financial%20advisor%20workspace&width=700&height=500&seq=ind-finance-1&orientation=landscape",
    metrics: [
      { value: "50%", label: "Lead Leakage Cut" },
      { value: "5h", label: "Saved Per Week" },
    ],
  },
];

const IndustriesPage = () => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="min-h-screen font-sans bg-white">
      <Navbar onBookCall={() => setModalOpen(true)} />

      {/* Page Hero */}
      <section className="relative bg-charcoal pt-40 pb-24">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20charcoal%20geometric%20grid%20blueprint%20pattern%20minimal%20enterprise%20technical%20background%20subtle%20lines%20texture%20architecture%20clean%20modern%20design%20dark%20gray&width=1440&height=400&seq=ind-hero-1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="inline-block bg-sapphire/20 text-sapphire text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6">
            Industries
          </div>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-white leading-tight max-w-3xl mb-5">
            Built for High-Value Service Businesses
          </h1>
          <p className="text-white/60 text-lg leading-relaxed max-w-2xl">
            Our systems are designed for industries where speed, follow-up, and conversion directly impact revenue.
          </p>
        </div>
      </section>

      {/* Industry Sections */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-24">
          {industries.map((ind, i) => (
            <div
              key={ind.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center`}
            >
              {/* Image */}
              <div className={`relative rounded-2xl overflow-hidden h-80 lg:h-[420px] ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <img
                  src={ind.image}
                  alt={ind.name}
                  className="w-full h-full object-cover object-top"
                />
                {/* Metrics overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-charcoal/90 via-charcoal/50 to-transparent p-6">
                  <div className="flex items-center gap-6">
                    {ind.metrics.map((m) => (
                      <div key={m.label}>
                        <div className="text-2xl font-extrabold text-white">{m.value}</div>
                        <div className="text-xs text-white/50 mt-0.5">{m.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-sapphire/10">
                    <i className={`${ind.icon} text-sapphire text-xl`}></i>
                  </div>
                  <span className="text-xs font-bold text-sapphire uppercase tracking-widest">{ind.name}</span>
                </div>

                <h2 className="text-3xl lg:text-4xl font-extrabold text-charcoal leading-tight mb-3">
                  {ind.tagline}
                </h2>

                {/* Pain Points */}
                <div className="mb-6">
                  <p className="text-xs font-bold text-charcoal/40 uppercase tracking-wider mb-3">Pain Points</p>
                  <ul className="space-y-2">
                    {ind.painPoints.map((p) => (
                      <li key={p} className="flex items-start gap-3">
                        <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className="ri-close-circle-line text-red-400 text-base"></i>
                        </span>
                        <span className="text-sm text-gray-600">{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Solution */}
                <div className="bg-sapphire/5 border border-sapphire/15 rounded-xl p-5 mb-4">
                  <p className="text-xs font-bold text-sapphire uppercase tracking-wider mb-2">Our Solution</p>
                  <p className="text-sm font-semibold text-charcoal">{ind.solution}</p>
                </div>

                {/* Outcome */}
                <div className="flex items-center gap-2 bg-charcoal rounded-lg px-5 py-3">
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-right-circle-fill text-sapphire text-base"></i>
                  </span>
                  <span className="text-sm font-bold text-white">{ind.outcome}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-5">
            See What This Looks Like in Your Business
          </h2>
          <p className="text-white/50 text-base max-w-lg mx-auto mb-10">
            We'll walk you through a system designed specifically for your industry, your volume, and your gaps.
          </p>
          <button
            onClick={() => setModalOpen(true)}
            className="bg-sapphire hover:bg-deep-sapphire text-white text-sm font-bold px-10 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            Book a Call
          </button>
        </div>
      </section>

      <Footer onBookCall={() => setModalOpen(true)} />
      <BookCallModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default IndustriesPage;
